import type { PairMetric } from '../web3/usePairMetrics';
import TokenIcon from './TokenIcon';
import Sparkline from './Sparkline';

const UP = '#22d3a8';
const DOWN = '#f5484f';

function formatPrice(v: number | undefined) {
  if (v === undefined) return '—';
  return `$${v.toLocaleString('en-US', { maximumFractionDigits: v < 1 ? 6 : 2 })}`;
}

// A continuously scrolling market strip — the single most recognizable "this is a
// real exchange" element. Reuses on-chain per-pair metrics; the track is rendered
// twice back-to-back so the CSS marquee loops seamlessly.
export default function MarketTickerTape({ metrics, loading }: { metrics: PairMetric[]; loading: boolean }) {
  if (loading) {
    return <div className="h-11 rounded-xl border border-abyss-700 bg-abyss-850 animate-pulse" />;
  }

  return (
    <div className="relative overflow-hidden rounded-xl border border-abyss-700 bg-abyss-850">
      {/* edge fades */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-12 z-10 bg-gradient-to-r from-abyss-850 to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-12 z-10 bg-gradient-to-l from-abyss-850 to-transparent" />
      <div className="flex whitespace-nowrap ticker-track">
        {[0, 1].map((copy) => (
          <div key={copy} className="flex shrink-0" aria-hidden={copy === 1}>
            {metrics.map((m) => (
              <TickerItem key={`${copy}-${m.pair.id}`} metric={m} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function TickerItem({ metric }: { metric: PairMetric }) {
  const isUp = metric.change24h >= 0;
  const color = isUp ? UP : DOWN;

  return (
    <div className="flex items-center gap-2.5 px-4 py-2.5 border-r border-abyss-700/60">
      <TokenIcon symbol={metric.pair.base} size={20} />
      <span className="text-sm font-medium text-foam-100">
        {metric.pair.base}
        <span className="text-foam-500">/{metric.pair.quote}</span>
      </span>
      <span className="text-sm tabular-nums text-foam-100">{formatPrice(metric.price)}</span>
      <span className={`text-xs font-semibold tabular-nums ${isUp ? 'text-tide-400' : 'text-coral-400'}`}>
        {isUp ? '+' : ''}
        {metric.change24h.toFixed(2)}%
      </span>
      <Sparkline data={metric.series.map((p) => p.price)} color={color} width={56} height={20} fill={false} />
    </div>
  );
}
